import React, { useState, useEffect } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ButtonGroup from "@mui/material/ButtonGroup";
import Select from "@mui/material/Select";
// NEW: Import Snackbar and MuiAlert for notifications
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import axios from "axios";
import { useAuth } from "../../hooks/AuthProvider";

import { LIST_TEMPLATES_ANALYTICS_ENDPOINT } from "../../config/urls";

import MessageNode from "../Whatsapp/MessageNode";
import { getMpmProductCount, getMpmThumbnailImage, getSpmProductName } from "../Whatsapp/mpmPreviewUtils";
import ButtonOne from "../../common/normal-button";
import AppDatePicker from "../../common/app-date-picker";
import AppChip from "../../common/app-chip";
import typography from "../../cssUtils/typography";
import ButtonTwo from "../../common/dashboard-button";

const getButtonColor = (index) => {
  const colors = [
    "#00BFA5",
    "#FF5252",
    "#651FFF",
    "#FFC107",
    "#3F51B5",
    "#009688",
  ];
  return colors[index % colors.length];
};

const TemplateAnalyticsTab = ({ template }) => {
  // State for date selection (from and to)
  const [fromDate, setFromDate] = useState(dayjs().subtract(6, "day"));
  const [toDate, setToDate] = useState(dayjs());

  const { getValidToken } = useAuth();

  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);
  // NEW: State for export functionality
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [exportMessageSeverity, setExportMessageSeverity] = useState("success");

  // Mock data for the graph (would be fetched from API in real implementation)
  const [analyticsData, setAnalyticsData] = useState([]);

  const [dateRangeError, setDateRangeError] = useState(false);

  // State for series visibility in chart
  const [visibleSeries, setVisibleSeries] = useState({
    messagesSent: true,
    messagesDelivered: true,
    messagesRead: true,
    replies: true,
  });

  // State for dropdown menu to toggle series
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Mock performance metrics
  const [performanceMetrics, setPerformanceMetrics] = useState({
    messagesSent: { count: 0, percentage: 0 },
    messagesDelivered: { count: 0, percentage: 0 },
    messagesRead: { count: 0, percentage: 0, readRate: 0 },
    replies: { count: 0, percentage: 0 },
    failed: { count: 0 },
    clicked: [],
  });

  // Failed message breakdown - reason list + per-reason daily chart
  const [failedBreakdown, setFailedBreakdown] = useState([]);
  const [failedChartData, setFailedChartData] = useState([]);

  const [buttonClicksView, setButtonClicksView] = useState("table"); // 'table' or 'graph'
  const [buttonClicks, setButtonClicks] = useState([]);
  const [buttonClicksLoading, setButtonClicksLoading] = useState(false);
  const [buttonClicksError, setButtonClicksError] = useState(null);
  const [buttonClicksGraphData, setButtonClicksGraphData] = useState([]);
  const [totalClicksType, setTotalClicksType] = useState("total"); // 'total' or 'unique'
  const [buttonAnchorEl, setButtonAnchorEl] = useState(null); // For button toggle menu
  const [visibleButtons, setVisibleButtons] = useState({}); // Track which buttons are visible in the graph

  // for API data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Reset error state
        setChartError(null);
        setMetricsError(null);
        setChartLoading(true);
        setMetricsLoading(true);

        // Format dates to YYYY-MM-DD
        const formattedFromDate = fromDate.format("YYYY-MM-DD");
        const formattedToDate = toDate.format("YYYY-MM-DD");

        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");

        console.log("template.category: ", template.category);

        // Construct query parameters - OLD
        let queryParams = new URLSearchParams({
          from_date: formattedFromDate,
          to_date: formattedToDate,
        });

        // Fetch chart data from the backend
        const response = await axios.get(
          `${LIST_TEMPLATES_ANALYTICS_ENDPOINT}/${template.template_id}/chart/?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-Tenant-Id": tenant,
            },
          },
        );

        // Update state with fetched data
        setAnalyticsData(response.data.analytics_data || []);
        setFailedBreakdown(response.data.failed_breakdown || []);
        setFailedChartData(response.data.failed_chart || []);

        // Get metrics data from response
        let apiMetrics = response.data.performance_metrics;

        // Add static percentages for frontend display (kept for UI consistency)
        const metricsWithPercentages = {
          messagesSent: {
            ...apiMetrics.messagesSent,
            percentage: 460, // Static value for UI consistency
          },
          messagesDelivered: {
            ...apiMetrics.messagesDelivered,
            percentage: 460, // Static value for UI consistency
          },
          messagesRead: {
            ...apiMetrics.messagesRead,
            percentage: 180, // Static value for UI consistency
          },
          replies: {
            ...apiMetrics.replies,
            percentage: 0, // Static value for UI consistency
          },
          failed: apiMetrics.failed || { count: 0 },
          clicked: apiMetrics.clicked || [],
        };

        console.log("metricsWithPercentages: ", metricsWithPercentages);

        // Update state with enhanced metrics
        setPerformanceMetrics(metricsWithPercentages);
      } catch (error) {
        console.error("Error fetching data:", error);
        // Set error message and empty data
        setChartError(
          error.response?.data?.error || "Failed to fetch chart data",
        );
        setMetricsError(
          error.response?.data?.error || "Failed to fetch metrics data",
        );
        setAnalyticsData([]);
        setFailedBreakdown([]);
        setFailedChartData([]);
        setPerformanceMetrics({
          messagesSent: { count: 0, percentage: 0 },
          messagesDelivered: { count: 0, percentage: 0 },
          messagesRead: { count: 0, percentage: 0, readRate: 0 },
          replies: { count: 0, percentage: 0 },
          failed: { count: 0 },
          clicked: [],
        });
      } finally {
        setChartLoading(false);
        setMetricsLoading(false);
      }
    };

    if (template?.template_id && !dateRangeError) {
      fetchData();
    }
  }, [fromDate, toDate, template?.template_id, getValidToken]);

  // for button click data
  useEffect(() => {
    console.log("performanceMetrics: ", performanceMetrics);

    // Check if we have button click data in performanceMetrics
    if (
      performanceMetrics &&
      performanceMetrics.clicked &&
      performanceMetrics.clicked.length > 0
    ) {
      console.log("Condition satisfied for buttonClicks");
      const clickedData = performanceMetrics.clicked;
      setButtonClicks(performanceMetrics.clicked);

      console.log("Button Clicks: ", clickedData);

      // Initialize visible buttons state
      const initialVisibleButtons = {};
      clickedData.forEach((button) => {
        initialVisibleButtons[button.button_content] = true;
      });
      setVisibleButtons(initialVisibleButtons);
    } else {
      setButtonClicks([]);
      setVisibleButtons({});
    }

    console.log("buttonClicks: ", buttonClicks);

    // Process button clicks data for graph
    if (analyticsData && analyticsData.length > 0 && analyticsData[0].clicked) {
      // Transform data for Recharts
      const graphData = analyticsData.map((day) => {
        const result = { date: day.date_iso };
        if (day.clicked && Array.isArray(day.clicked)) {
          day.clicked.forEach((button) => {
            // Use button content as the key
            result[button.button_content] = button.count;
          });
        }
        return result;
      });

      setButtonClicksGraphData(graphData);
    }
  }, [performanceMetrics, analyticsData]);

  // helper function to generate mock chart data
  const generateMockChartData = () => {
    const startDate = dayjs(fromDate).isAfter(toDate) ? toDate : fromDate;
    const endDate = dayjs(fromDate).isAfter(toDate) ? fromDate : toDate;

    // Calculate the number of days between dates
    const days = endDate.diff(startDate, "day") + 1;

    const data = [];
    for (let i = 0; i < days; i++) {
      const currentDate = dayjs(startDate).add(i, "day");
      const formattedDate = currentDate.format("D MMM");

      // Generate random data
      const sentCount = Math.floor(Math.random() * 20) + 5;
      const deliveredCount = sentCount - Math.floor(Math.random() * 3);
      const readCount = deliveredCount - Math.floor(Math.random() * 5);
      const replyCount = Math.floor(Math.random() * 3);

      data.push({
        date: formattedDate,
        messagesSent: sentCount,
        messagesDelivered: deliveredCount,
        messagesRead: readCount,
        replies: replyCount,
      });
    }

    return data;
  };

  // Function to validate date range (add this before the return statement in the component)
  const validateDateRange = (fromDate, toDate) => {
    const daysDifference = toDate.diff(fromDate, "day");
    return daysDifference <= 30;
  };

  // Handlers
  // Modified date change handlers (replace the existing handlers)
  const handleFromDateChange = (newDate) => {
    if (validateDateRange(newDate, toDate)) {
      setFromDate(newDate);
      setDateRangeError(false);
    } else {
      setFromDate(newDate);
      setDateRangeError(true);
      setChartError("Date range cannot be more than 30 days.");
      setMetricsError("Date range cannot be more than 30 days.");
      setButtonClicksError("Date range cannot be more than 30 days.");
    }
  };

  const handleToDateChange = (newDate) => {
    if (validateDateRange(fromDate, newDate)) {
      setToDate(newDate);
      setDateRangeError(false);
    } else {
      setToDate(newDate);
      setDateRangeError(true);
      setChartError("Date range cannot be more than 30 days.");
      setMetricsError("Date range cannot be more than 30 days.");
      setButtonClicksError("Date range cannot be more than 30 days.");
    }
  };

  const handleButtonClicksViewChange = (view) => {
    setButtonClicksView(view);
  };

  const handleTotalClicksTypeChange = (type) => {
    setTotalClicksType(type);
  };

  // Handlers for the button toggle menu
  const handleButtonToggleClick = (event) => {
    setButtonAnchorEl(event.currentTarget);
  };

  const handleButtonToggleClose = () => {
    setButtonAnchorEl(null);
  };

  const handleButtonToggle = (buttonName) => {
    setVisibleButtons({
      ...visibleButtons,
      [buttonName]: !visibleButtons[buttonName],
    });
  };

  // MODIFIED: handleExportCSV function with loading and notification logic
  const handleExportCSV = async () => {
    // Step 1: Set loading state and clear previous messages
    setIsExporting(true);
    setExportMessage("");

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const formattedFromDate = fromDate.format("YYYY-MM-DD");
      const formattedToDate = toDate.format("YYYY-MM-DD");

      let queryParams = new URLSearchParams({
        from_date: formattedFromDate,
        to_date: formattedToDate,
      });

      // Make API request to export endpoint
      const response = await fetch(
        `${LIST_TEMPLATES_ANALYTICS_ENDPOINT}/${template.template_id}/export/?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenant,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to export analytics data");
      }

      // Create a blob from the response
      const blob = await response.blob();

      // Create a link element to download the file
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `template-analytics-${template.template_name}-${formattedFromDate}-to-${formattedToDate}.csv`;

      // Append to the document and trigger download
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Step 2: Set success message and severity
      setExportMessage("Export completed successfully");
      setExportMessageSeverity("success");
    } catch (error) {
      console.error("Error exporting analytics data:", error.message);
      // Step 3: Set error message and severity
      setExportMessage("Failed to export analytics data. Please try again.");
      setExportMessageSeverity("error");
    } finally {
      // Step 4: Always reset the loading state
      setIsExporting(false);
    }
  };

  const handleEditTemplate = () => {
    console.log("Edit template clicked for:", template);
    // This would navigate to template editing page in the real implementation
  };

  const handleSeriesToggleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSeriesToggleClose = () => {
    setAnchorEl(null);
  };

  const handleSeriesToggle = (series) => {
    setVisibleSeries({
      ...visibleSeries,
      [series]: !visibleSeries[series],
    });
  };

  // NEW: Handler for closing the snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setExportMessage("");
  };

  // Component for metric display
  const MetricDisplay = ({ label, count, percentage, readRate }) => (
    <Box sx={{ textAlign: "center", p: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle1">{label}</Typography>
        <Tooltip title={`This shows the ${label.toLowerCase()}`}>
          <InfoIcon fontSize="small" sx={{ ml: 1, color: "text.secondary" }} />
        </Tooltip>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "center",
        }}
      >
        <Typography variant="h4" component="span" sx={{ mr: 1 }}>
          {dateRangeError
            ? "NA"
            : readRate !== undefined
              ? `${count} (${readRate}%)`
              : count}
        </Typography>
        {/* {percentage > 0 && (
            <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center' }}>
                ↑ {percentage}%
            </Typography>
            )} */}
      </Box>
    </Box>
  );

  // CustomTooltip component for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, boxShadow: 3 }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              style={{ color: entry.color }}
            >
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper sx={{ p: 3, width: "100%" }}>
      {/* Template Header - Name, Language, Status, etc. */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ ...typography.h1 }}>
              {template?.template_name || "Template Name"} •{" "}
              {template?.language || "English"}
            </Typography>
            <AppChip
              label={template?.status || "Active - Quality Pending"}
              color="success"
              sx={{ ml: 2 }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {exportMessage && (
              <MuiAlert
                onClose={handleCloseSnackbar}
                severity={exportMessageSeverity}
                elevation={6}
                variant="filled"
                sx={{
                  height: "fit-content",
                  py: 0.5,
                  px: 1.5,
                  fontSize: "0.85rem",
                }}
              >
                {exportMessage}
              </MuiAlert>
            )}
            {/* TODO: Temporarily disabled export button */}
            <ButtonOne
              variant="contained"
              startIcon={isExporting ? null : <DownloadIcon />}
              onClick={handleExportCSV}
              disabled={dateRangeError || isExporting}
            >
              {isExporting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Export CSV"
              )}
            </ButtonOne>
          </Box>
        </Box>
        <Typography sx={{ ...typography.bodyLarge }}>
          {template?.category || "Utility"}
        </Typography>
      </Box>

      {/* Main content grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3.5}>
          <Paper
            sx={{
              p: 2,
              mb: 3,
              minHeight: 300,
              maxHeight: 600,
              bgcolor: "grey.100",
              overflowY: "auto",
            }}
          >
            <Typography sx={{ ...typography.h2, mb: 2 }}>
              Your template
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              {template.template_preview_json.marketingOption &&
              template.template_preview_json.marketingOption === "carousel" ? (
                <MessageNode
                  templateName={template.template_preview_json.templateName}
                  language={template.template_preview_json.language}
                  headerType="none"
                  headerText=""
                  headerFile={null}
                  body=""
                  sampleBodyFields={[]}
                  footer=""
                  mode="view"
                  allButtons={[]}
                  // Carousel-specific props
                  marketingOption="carousel"
                  carouselCards={template.template_preview_json.carouselCards}
                  carouselMainBody={
                    template.template_preview_json.carouselMainBody
                  }
                  carouselMainBodySampleFields={
                    template.template_preview_json
                      .carouselMainBodySampleFields || []
                  }
                  // Create cardFile props dynamically based on available files in jsonData
                  {...Object.keys(template.template_preview_json)
                    .filter((key) => key.startsWith("cardFile_"))
                    .reduce((acc, key) => {
                      acc[key] = template.template_preview_json[key];
                      return acc;
                    }, {})}
                />
              ) : (
                <MessageNode
                  templateName={template.template_preview_json.templateName}
                  marketingOption={
                    template.template_preview_json.marketingOption || null
                  }
                  language={template.template_preview_json.language}
                  headerType={template.template_preview_json.headerType}
                  headerText={template.template_preview_json.headerText}
                  headerFile={template.template_preview_json.headerFile}
                  body={template.template_preview_json.body}
                  sampleBodyFields={
                    template.template_preview_json.sampleBodyFields
                  }
                  sampleHeaderFields={
                    template.template_preview_json.sampleHeaderFields
                  }
                  footer={template.template_preview_json.footer}
                  allButtons={template.template_preview_json.allButtons}
                  showSampleValues={true} // Use sample values mode
                  mode="view"
                  location={template.template_preview_json.location}
                  productCount={getMpmProductCount(
                    template?.template_preview_json?.mpmSections,
                  )}
                  thumbnailImage={getMpmThumbnailImage(
                    template?.template_preview_json?.mpmThumbnail || template?.template_preview_json?.spmProduct,
                  )}
                  productName={getSpmProductName(template?.template_preview_json?.spmProduct)}
                />
              )}
            </Box>
          </Paper>

          {/* Top Block Reason Section (placeholder) */}
          <Paper sx={{ p: 2, minHeight: 100, bgcolor: "grey.100" }}>
            <Typography sx={{ mb: 2, ...typography.h2, display: "flex", flexDirection: "row", alignItems: "center" }}>
              <span>Top block reason</span>
              <Tooltip title="Most common reasons why messages were blocked">
                <InfoIcon
                  fontSize="small"
                  sx={{ ml: 1, color: "text.secondary" }}
                />
              </Tooltip>
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                --
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={8.5}>
          {/* Performance Metrics Section */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography sx={{ ...typography.h2 }}>Performance</Typography>
                <Tooltip title="Performance metrics for this template">
                  <InfoIcon
                    fontSize="small"
                    sx={{ ml: 1, color: "text.secondary" }}
                  />
                </Tooltip>
              </Box>

              {/* Date Range Selectors */}
              <Box>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ mb: 0.5, display: "block" }}
                      >
                        From Date
                      </Typography>
                      <AppDatePicker
                        value={fromDate}
                        onChange={handleFromDateChange}
                        error={dateRangeError}
                        slotProps={{ textField: { sx: { width: 150 } } }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ mb: 0.5, display: "block" }}
                      >
                        To Date
                      </Typography>
                      <AppDatePicker
                        value={toDate}
                        onChange={handleToDateChange}
                        error={dateRangeError}
                        slotProps={{ textField: { sx: { width: 150 } } }}
                      />
                    </Box>
                  </Stack>
                </LocalizationProvider>

                {dateRangeError && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ display: "block", mt: 1 }}
                  >
                    Date range cannot be more than 30 days
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Metrics Display */}
            {metricsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : metricsError ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography sx={{ ...typography.bodyLarge, color: "red" }}>
                  {metricsError}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={2.4}>
                  <MetricDisplay
                    label="Messages sent"
                    count={performanceMetrics.messagesSent.count}
                    // percentage={performanceMetrics.messagesSent.percentage}
                  />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <MetricDisplay
                    label="Messages delivered"
                    count={performanceMetrics.messagesDelivered.count}
                    // percentage={performanceMetrics.messagesDelivered.percentage}
                  />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <MetricDisplay
                    label="Messages read"
                    count={performanceMetrics.messagesRead.count}
                    percentage={performanceMetrics.messagesRead.percentage}
                    // readRate={performanceMetrics.messagesRead.readRate}
                  />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <MetricDisplay
                    label="Replies"
                    count={performanceMetrics.replies.count}
                    // percentage={performanceMetrics.replies.percentage}
                  />
                </Grid>
                <Grid item xs={12} sm={2.4}>
                  <MetricDisplay
                    label="Failed"
                    count={performanceMetrics.failed.count}
                  />
                </Grid>
              </Grid>
            )}
          </Paper>

          {/* Graphical Representation */}
          <Paper sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography sx={{ ...typography.h2 }}>
                Message performance over time
              </Typography>

              {/* Toggle series dropdown */}
              <Box>
                <ButtonTwo
                  variant="outlined"
                  size="small"
                  onClick={handleSeriesToggleClick}
                  endIcon={<MoreVertIcon />}
                >
                  Toggle metrics
                </ButtonTwo>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleSeriesToggleClose}
                >
                  <MenuItem onClick={() => handleSeriesToggle("messagesSent")}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visibleSeries.messagesSent}
                          onChange={() => handleSeriesToggle("messagesSent")}
                          sx={{ mr: 1 }}
                        />
                      }
                      label="Messages sent"
                    />
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleSeriesToggle("messagesDelivered")}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visibleSeries.messagesDelivered}
                          onChange={() =>
                            handleSeriesToggle("messagesDelivered")
                          }
                          sx={{ mr: 1 }}
                        />
                      }
                      label="Messages delivered"
                    />
                  </MenuItem>
                  <MenuItem onClick={() => handleSeriesToggle("messagesRead")}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visibleSeries.messagesRead}
                          onChange={() => handleSeriesToggle("messagesRead")}
                          sx={{ mr: 1 }}
                        />
                      }
                      label="Messages read"
                    />
                  </MenuItem>
                  <MenuItem onClick={() => handleSeriesToggle("replies")}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visibleSeries.replies}
                          onChange={() => handleSeriesToggle("replies")}
                          sx={{ mr: 1 }}
                        />
                      }
                      label="Replies"
                    />
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {/* Line Chart */}
            {chartLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : chartError ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography sx={{ ...typography.bodyLarge, color: "red" }}>
                  {chartError}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: 300, width: "100%" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={analyticsData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend />
                    {visibleSeries.messagesSent && (
                      <Line
                        type="monotone"
                        dataKey="messagesSent"
                        stroke="#FF5252"
                        name="Messages sent"
                        activeDot={{ r: 8 }}
                      />
                    )}
                    {visibleSeries.messagesDelivered && (
                      <Line
                        type="monotone"
                        dataKey="messagesDelivered"
                        stroke="#651FFF"
                        name="Messages delivered"
                      />
                    )}
                    {visibleSeries.messagesRead && (
                      <Line
                        type="monotone"
                        dataKey="messagesRead"
                        stroke="#00BFA5"
                        name="Messages read"
                      />
                    )}
                    {visibleSeries.replies && (
                      <Line
                        type="monotone"
                        dataKey="replies"
                        stroke="#00796B"
                        name="Replies"
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          {/* Error Messages Section - Only show if there are failed messages in range */}
          {failedBreakdown.length > 0 && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography sx={{ ...typography.h2 }}>Error messages</Typography>
                <Tooltip title="Reasons why messages sent using this template failed to be delivered">
                  <InfoIcon
                    fontSize="small"
                    sx={{ ml: 1, color: "text.secondary" }}
                  />
                </Tooltip>
              </Box>

              {chartLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Box sx={{ height: 300, width: "100%", mb: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={failedChartData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        {failedBreakdown.map((item, index) => (
                          <Line
                            key={item.reason}
                            type="monotone"
                            dataKey={item.reason}
                            stroke={getButtonColor(index)}
                            name={item.reason}
                            dot={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>

                  <Stack spacing={1}>
                    {failedBreakdown.map((item, index) => (
                      <Box
                        key={item.reason}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          pb: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box
                            sx={{
                              width: 14,
                              height: 3,
                              backgroundColor: getButtonColor(index),
                              mr: 1.5,
                            }}
                          />
                          <Typography variant="body2">
                            {item.reason}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.count.toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
            </Paper>
          )}

          {/* Button Clicks Section - Only show if the template has buttons */}
          {buttonClicks.length > 0 && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="subtitle1">Button clicks</Typography>
                  <Tooltip title="Button clicks are reported up to 7 days after a message is sent.">
                    <InfoIcon
                      fontSize="small"
                      sx={{ ml: 1, color: "text.secondary" }}
                    />
                  </Tooltip>
                </Box>

                {/* <p>{template?.category}</p> */}

                {/* View toggle buttons */}
                <Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {/* Total clicks type selector (shown only for marketing templates) */}
                    {template?.category === "MARKETING" && (
                      <FormControl size="small">
                        <Select
                          value={totalClicksType}
                          onChange={(e) =>
                            handleTotalClicksTypeChange(e.target.value)
                          }
                          displayEmpty
                          sx={{ minWidth: 130 }}
                        >
                          <MenuItem value="total">Total clicks</MenuItem>
                          {/* <MenuItem value="unique">Unique clicks</MenuItem> */}
                        </Select>
                      </FormControl>
                    )}

                    {/* Table/Graph toggle */}
                    <ButtonGroup variant="outlined" size="small">
                      <Button
                        variant={
                          buttonClicksView === "table"
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() => handleButtonClicksViewChange("table")}
                      >
                        Table
                      </Button>
                      <Button
                        variant={
                          buttonClicksView === "graph"
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() => handleButtonClicksViewChange("graph")}
                      >
                        Graph
                      </Button>
                    </ButtonGroup>
                  </Box>
                </Box>
              </Box>

              {/* Display based on view selection */}
              {buttonClicksLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : dateRangeError ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography color="error">{buttonClicksError}</Typography>
                </Box>
              ) : buttonClicksView === "table" ? (
                /* Table View */
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Label</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Total clicks</TableCell>
                        <TableCell>Clicks vs previous period</TableCell>
                        <TableCell>Click rate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {buttonClicks.length > 0 ? (
                        buttonClicks.map((button, index) => (
                          <TableRow key={index}>
                            <TableCell>{button.button_content}</TableCell>
                            <TableCell>{button.type}</TableCell>
                            <TableCell>{button.count}</TableCell>
                            <TableCell>--</TableCell>
                            <TableCell>
                              {performanceMetrics.messagesSent.count > 0
                                ? `${Math.round((button.count / performanceMetrics.messagesSent.count) * 100)}%`
                                : "0%"}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No button click data available
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <>
                  {/* Button metrics display and toggle */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    {/* Individual button metrics display */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {buttonClicks.map((button, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                          <Box
                            sx={{
                              textAlign: "center",
                              p: 2,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                              backgroundColor: visibleButtons[
                                button.button_content
                              ]
                                ? "rgba(0, 0, 0, 0.04)"
                                : "transparent",
                            }}
                          >
                            <Typography variant="subtitle2">
                              {button.button_content}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              {button.type.replace("_", " ")}
                            </Typography>
                            <Typography variant="h5" sx={{ mt: 1 }}>
                              {button.count}{" "}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                              >
                                (
                                {performanceMetrics.messagesSent.count > 0
                                  ? `${Math.round((button.count / performanceMetrics.messagesSent.count) * 100)}%`
                                  : "0%"}
                                )
                              </Typography>
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    {/* Toggle buttons control */}
                    <Box>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleButtonToggleClick}
                        endIcon={<MoreVertIcon />}
                        sx={{ width: 170 }}
                      >
                        Toggle buttons
                      </Button>
                      <Menu
                        anchorEl={buttonAnchorEl}
                        open={Boolean(buttonAnchorEl)}
                        onClose={handleButtonToggleClose}
                      >
                        {buttonClicks.map((button, index) => (
                          <MenuItem
                            key={index}
                            onClick={() =>
                              handleButtonToggle(button.button_content)
                            }
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    visibleButtons[button.button_content]
                                  }
                                  onChange={() =>
                                    handleButtonToggle(button.button_content)
                                  }
                                  sx={{ mr: 1 }}
                                />
                              }
                              label={button.button_content}
                            />
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  </Box>

                  {/* Line Chart */}
                  <Box sx={{ height: 300, width: "100%" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={buttonClicksGraphData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend />
                        {buttonClicks.map(
                          (button, index) =>
                            visibleButtons[button.button_content] && (
                              <Line
                                key={index}
                                type="monotone"
                                dataKey={button.button_content}
                                stroke={getButtonColor(index)}
                                name={button.button_content}
                              />
                            ),
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* NEW: Snackbar component for displaying success/error messages */}
      {/* <Snackbar
            open={!!exportMessage} // Open if exportMessage is not an empty string
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <MuiAlert 
                onClose={handleCloseSnackbar} 
                severity={exportMessageSeverity} 
                elevation={6} 
                variant="filled"
            >
                {exportMessage}
            </MuiAlert>
        </Snackbar> */}
    </Paper>
  );
};

export default TemplateAnalyticsTab;
